<template>
    <Spinner v-if="displaySpinner" />
    <div class="title-and-controls">
        <EditableTitle v-model="businessEntity" icon="pi pi-building" />
        <div class="controls">
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                class="p-button-secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateBusinessEntityDescription"
            />
        </div>
    </div>
    <section :class="['description', { 'edit': isEditing }]">
        <TextAppArtefact
            v-if="businessEntity?.identity && description"
            v-model="description"
            :entityId="businessEntity.identity"
            :entityType="EntityType.BUSINESS_ENTITY"
            :isEditing="isEditing"
            :stekzModel="businessEntity"
            ref="textAppArtefact"
        />
    </section>

    <section :class="['definition', { 'edit': isEditing }]">
        <DataTable
            v-if="definition?.content"
            v-model:value="definitionRows"
            :editMode="isEditing ? 'cell' : undefined"
            @cell-edit-complete="onCellEditComplete"
            tableStyle="width: 100%"
        >
            <Column field="name" header="Name" style="width: 200px">
                <template #editor="{ data, field }">
                    <InputText v-model="data[field]" class="w-full" />
                </template>
            </Column>
            <Column field="type" header="Type" style="width: 150px">
                <template #body="{ data }">
                    {{
                        fieldTypes.find((t) => t.value === data.type)?.label ||
                        data.type
                    }}
                </template>
                <template #editor="{ data, field }">
                    <Dropdown
                        v-model="data[field]"
                        :options="fieldTypes"
                        optionLabel="label"
                        optionValue="value"
                        class="w-full"
                    />
                </template>
            </Column>
            <Column field="description" header="Description">
                <template #editor="{ data, field }">
                    <InputText v-model="data[field]" class="w-full" />
                </template>
            </Column>
            <Column
                v-if="isEditing"
                :exportable="false"
                style="width: 80px"
                bodyStyle="text-align: right"
            >
                <template #body="{ data }">
                    <Button
                        icon="pi pi-trash"
                        outlined
                        severity="danger"
                        @click="removeRow(data)"
                    />
                </template>
            </Column>
        </DataTable>
        <Button
            v-if="isEditing"
            icon="pi pi-plus"
            label="Add New Field"
            @click="addNewRow"
        />
    </section>
</template>

<script setup lang="ts">
import Spinner from "@/shared/Spinner.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import { ref, watch, computed } from "vue";
import { useEditManager } from "@/composables/edit-manager";
import { EntityType } from "@/stores/entity-store";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { useBusinessEntityManager } from "@/stores/business-entity-store";
import DataTable, { DataTableCellEditCompleteEvent } from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";

const { isEditing } = useEditManager();
const { businessEntity, isFetching, description, definition } =
    useBusinessEntityManager();
const { generateContent } = useGenerateDescriptionDialog();

const displaySpinner = ref(false);

interface DefinitionRow {
    name: string;
    type: string;
    description: string;
}

interface DefinitionField {
    type: string;
    description: string;
}

interface DefinitionContent {
    [key: string]: DefinitionField;
}

const fieldTypes = [
    { label: "String", value: "string" },
    { label: "Number", value: "number" },
    { label: "Integer", value: "integer" },
    { label: "Boolean", value: "boolean" },
];

const definitionRows = computed<DefinitionRow[]>({
    get: () => {
        if (!definition.value?.content) return [];
        try {
            const content: DefinitionContent = JSON.parse(
                definition.value.content
            );
            return Object.entries(content).map(([name, field]) => ({
                name,
                type: field.type,
                description: field.description,
            }));
        } catch {
            return [];
        }
    },
    set: (newValue: DefinitionRow[]) => {
        if (definition.value) {
            const content: DefinitionContent = {};
            newValue.forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
            definition.value.content = JSON.stringify(content);
        }
    },
});

const onCellEditComplete = (event: DataTableCellEditCompleteEvent) => {
    let { data, newValue, field } = event;
    if (typeof field === "string" && field in data) {
        data[field as keyof DefinitionRow] = newValue;
        if (definition.value) {
            const content: DefinitionContent = {};
            definitionRows.value.forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
            definition.value.content = JSON.stringify(content);
        }
    }
};

watch(
    isFetching,

    (newValue) => {
        displaySpinner.value = newValue;
    },
    {
        immediate: true,
    }
);

const generateBusinessEntityDescription = async () => {
    if (!businessEntity.value) {
        return;
    }
    await generateContent(
        businessEntity.value.identity,
        EntityType.BUSINESS_ENTITY,
        description.value?.updateContent.bind(description.value)
    );
};

const addNewRow = () => {
    const newRow: DefinitionRow = {
        name: `field_${definitionRows.value.length + 1}`,
        type: "string",
        description: "",
    };

    if (definition.value) {
        const content: DefinitionContent = {};
        [...definitionRows.value, newRow].forEach((row) => {
            content[row.name] = {
                type: row.type,
                description: row.description,
            };
        });
        definition.value.content = JSON.stringify(content);
    }
};

const removeRow = (rowData: DefinitionRow) => {
    if (definition.value) {
        const content: DefinitionContent = {};
        definitionRows.value
            .filter((row) => row.name !== rowData.name)
            .forEach((row) => {
                content[row.name] = {
                    type: row.type,
                    description: row.description,
                };
            });
        definition.value.content = JSON.stringify(content);
    }
};
</script>

<style scoped lang="scss">
.controls {
    text-wrap-mode: nowrap;
}

section {
    border: 1px solid transparent;
    padding: 1rem;

    &.description {
        padding-top: 0;
        z-index: 3;
    }

    &.edit {
        border-color: var(--stekz-outline);
        border-radius: 0.5rem;

        &.description {
            padding: 0;
        }
    }

    &.definition {
        margin-top: 1rem;

        h2 {
            margin-top: 0;
            margin-bottom: 1rem;
        }
    }
}
</style>
